// The terrain pyramid's tile tree: cubesphere quadtree addressing, and the
// streaming bookkeeping the surface is drawn from.
//
// Two halves, one contract. Addressing is pure geometry over the quad key
// `<body>/<face>/<L>/<x>/<y>` and the equal-angle cube projection; the bake side
// carries a Python twin of it written from the same doc, and the two must agree
// key for key. The runtime half owns the screen-space-error traversal, the fetch
// priority queue and the LRU by bytes - bookkeeping only, so the network and the
// decode arrive as injected functions and no renderer is touched. Vectors are
// plain number tuples and nothing here imports three.js, which is what lets
// tools/preview/tiletree-check.mjs run the whole module under node.
//
// Design: docs/roadmap/03-terrain-pyramid.md, "Tile scheme" and "The runtime".
// Contract for consumers: docs/ATLAS-TERRAIN.md, "Tile tree contract".

import { clamp } from '../math/angles.ts';
import { track } from '../visits/bus.ts';

/** Metres or a unit direction, body-fixed frame: +Z the north pole, +X through lon 0. */
export type Vec3 = readonly [number, number, number];

/**
 * One tile. `face` is 0..5 = +X, -X, +Y, -Y, +Z, -Z; `x` and `y` index the
 * level's `2^L x 2^L` grid of equal-angle face coordinates, u before v.
 */
export interface TileKey {
  readonly body: string;
  readonly face: number;
  readonly level: number;
  readonly x: number;
  readonly y: number;
}

/** Height samples across one tile edge, apron excluded. */
export const TILE_HEIGHT_SAMPLES = 256;
/** Albedo texels across one tile edge: two per height sample. */
export const TILE_ALBEDO_TEXELS = 512;
/** One sample of apron per edge, so normals and the horizon march reach the boundary. */
export const TILE_APRON_SAMPLES = 1;
/** Claim texels across one tile edge: coarse on purpose - a boundary line, not this texture, is
 *  what carries a sharp edge. */
export const TILE_CLAIM_TEXELS = 64;
export const FACE_COUNT = 6;

const QUARTER = Math.PI / 4;
const HALF_PI = Math.PI / 2;

// -----------------------------------------------------------------------------
// Addressing
// -----------------------------------------------------------------------------

/** Tiles across one face edge at level L. */
export function tilesAcross(level: number): number {
  return 2 ** level;
}

/** Tiles at level L across all six faces. */
export function tilesAtLevel(level: number): number {
  return FACE_COUNT * 4 ** level;
}

/** Tiles from level 0 through L inclusive, all six faces. */
export function tilesThroughLevel(level: number): number {
  return 2 * (4 ** (level + 1) - 1);
}

/**
 * Metres per height sample at level L on a datum of radius R.
 *
 * A face spans a quarter of a great circle, arc `pi*R/2`, and a level-L face is
 * `TILE_HEIGHT_SAMPLES * 2^L` samples across. Sample spacing follows from the
 * datum radius and nothing else.
 */
export function gsd(level: number, radiusM: number): number {
  return (Math.PI * radiusM) / (2 * TILE_HEIGHT_SAMPLES * 2 ** level);
}

/** Metres per albedo texel at level L: half the height spacing. */
export function albedoGsd(level: number, radiusM: number): number {
  return gsd(level, radiusM) / 2;
}

/**
 * The shallowest level at least as fine as a product's native posting, so the
 * pyramid never claims to be coarser than the data. Levels are powers of two
 * and postings are not, so a product lands within a factor of two of a level.
 */
export function levelForPosting(
  postingM: number,
  radiusM: number,
  kind: 'height' | 'albedo' = 'height',
): number {
  const samples = kind === 'albedo' ? TILE_ALBEDO_TEXELS : TILE_HEIGHT_SAMPLES;
  const level = Math.ceil(Math.log2((Math.PI * radiusM) / (2 * samples * postingM)));
  return Math.max(0, level);
}

/** How much a level oversamples a product at that posting. 1.0 is exact. */
export function oversampleAt(
  level: number,
  postingM: number,
  radiusM: number,
  kind: 'height' | 'albedo' = 'height',
): number {
  return postingM / (kind === 'albedo' ? albedoGsd(level, radiusM) : gsd(level, radiusM));
}

export function formatTileKey(key: TileKey): string {
  return `${key.body}/${key.face}/${key.level}/${key.x}/${key.y}`;
}

export function parseTileKey(s: string): TileKey {
  const parts = s.split('/');
  if (parts.length !== 5) throw new Error(`tile key needs 5 fields, got ${parts.length}: ${s}`);
  const [body, faceS, levelS, xS, yS] = parts as [string, string, string, string, string];
  const face = Number(faceS);
  const level = Number(levelS);
  const x = Number(xS);
  const y = Number(yS);
  if (!body) throw new Error(`tile key has no body: ${s}`);
  for (const [name, v] of [['face', face], ['level', level], ['x', x], ['y', y]] as const) {
    if (!Number.isInteger(v) || v < 0) throw new Error(`tile key ${name} is not a non-negative integer: ${s}`);
  }
  if (face >= FACE_COUNT) throw new Error(`tile key face is not 0..5: ${s}`);
  const n = tilesAcross(level);
  if (x >= n || y >= n) throw new Error(`tile key x/y outside the level's ${n}x${n} grid: ${s}`);
  return { body, face, level, x, y };
}

/** Equal-angle face coordinates, each 0..1 across the face. */
export interface FaceUv {
  readonly face: number;
  readonly u: number;
  readonly v: number;
}

function normAngle(a: number): number {
  return (a + QUARTER) / HALF_PI;
}

function denormAngle(t: number): number {
  return t * HALF_PI - QUARTER;
}

/**
 * Direction to face and equal-angle coordinates.
 *
 * The major axis is the component of largest magnitude; the other two, divided
 * by its magnitude and taken in ascending axis order (x before y before z),
 * are the tangents of the two face angles, each in [-pi/4, pi/4]. A component
 * within a 1e-12 relative tolerance of the largest is treated as tied and the
 * lower axis index wins, matching quadkey.py so libm rounding cannot split a
 * cube corner between two faces.
 */
export function faceUvForDirection(dir: Vec3): FaceUv {
  const [x, y, z] = dir;
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const az = Math.abs(z);
  const m = Math.max(ax, ay, az);
  const eps = m * 1e-12;
  let face: number;
  let major: number;
  let un: number;
  let vn: number;
  if (ax >= m - eps) {
    face = x >= 0 ? 0 : 1;
    major = ax;
    un = y;
    vn = z;
  } else if (ay >= m - eps) {
    face = y >= 0 ? 2 : 3;
    major = ay;
    un = x;
    vn = z;
  } else {
    face = z >= 0 ? 4 : 5;
    major = az;
    un = x;
    vn = y;
  }
  if (major === 0) throw new Error('faceUvForDirection: the zero vector has no face');
  return { face, u: normAngle(Math.atan(un / major)), v: normAngle(Math.atan(vn / major)) };
}

/**
 * The equal-angle projection's face coordinate: `tan` of the angle a face
 * coordinate in 0..1 stands for.
 *
 * `directionForFaceUv` is this pair permuted onto the face's axes and
 * normalised. A renderer meshing a tile row by row tabulates the tangents
 * instead, since the u angle depends on u alone, and reads them here rather
 * than re-deriving where the quarter turn goes.
 */
export function faceTangent(t: number): number {
  return Math.tan(denormAngle(t));
}

/**
 * Face and equal-angle coordinates back to a unit direction.
 *
 * u and v outside 0..1 are legal and land on a neighbouring face's ground,
 * which is how `neighboursOf` crosses a face edge.
 */
export function directionForFaceUv(face: number, u: number, v: number): Vec3 {
  const a = faceTangent(u);
  const b = faceTangent(v);
  const s = face % 2 === 0 ? 1 : -1;
  const p: [number, number, number] =
    face < 2 ? [s, a, b] : face < 4 ? [a, s, b] : [a, b, s];
  const inv = 1 / Math.hypot(p[0], p[1], p[2]);
  return [p[0] * inv, p[1] * inv, p[2] * inv];
}

export function directionForLatLon(latRad: number, lonRad: number): Vec3 {
  const c = Math.cos(latRad);
  return [c * Math.cos(lonRad), c * Math.sin(lonRad), Math.sin(latRad)];
}

export function latLonForDirection(dir: Vec3): { latRad: number; lonRad: number } {
  const [x, y, z] = dir;
  const r = Math.hypot(x, y, z);
  return { latRad: Math.asin(clamp(z / r, -1, 1)), lonRad: Math.atan2(y, x) };
}

function indexOnFace(t: number, n: number): number {
  const i = Math.floor(t * n);
  return i < 0 ? 0 : i >= n ? n - 1 : i;
}

export function tileForDirection(body: string, dir: Vec3, level: number): TileKey {
  const f = faceUvForDirection(dir);
  const n = tilesAcross(level);
  return { body, face: f.face, level, x: indexOnFace(f.u, n), y: indexOnFace(f.v, n) };
}

export function tileForLatLon(body: string, latRad: number, lonRad: number, level: number): TileKey {
  return tileForDirection(body, directionForLatLon(latRad, lonRad), level);
}

export function parentOf(key: TileKey): TileKey | null {
  if (key.level === 0) return null;
  return { body: key.body, face: key.face, level: key.level - 1, x: key.x >> 1, y: key.y >> 1 };
}

export function childrenOf(key: TileKey): readonly [TileKey, TileKey, TileKey, TileKey] {
  const level = key.level + 1;
  const x = key.x * 2;
  const y = key.y * 2;
  const { body, face } = key;
  return [
    { body, face, level, x, y },
    { body, face, level, x: x + 1, y },
    { body, face, level, x, y: y + 1 },
    { body, face, level, x: x + 1, y: y + 1 },
  ];
}

export interface TileBounds {
  readonly face: number;
  readonly level: number;
  /** The tile's equal-angle extent on its face, each 0..1. */
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
  /** Unit direction at the tile centre. */
  readonly centre: Vec3;
  /** Unit directions at (u0,v0), (u1,v0), (u0,v1), (u1,v1). */
  readonly corners: readonly [Vec3, Vec3, Vec3, Vec3];
  /** Great-circle angle from the centre to the furthest corner, radians. */
  readonly angularRadius: number;
  /** The centre on the datum sphere, metres, body-fixed. */
  readonly centreM: Vec3;
  /** Bounding-sphere radius about `centreM`, metres. */
  readonly boundRadiusM: number;
}

export function boundsOf(key: TileKey, radiusM: number): TileBounds {
  const n = tilesAcross(key.level);
  const u0 = key.x / n;
  const u1 = (key.x + 1) / n;
  const v0 = key.y / n;
  const v1 = (key.y + 1) / n;
  const centre = directionForFaceUv(key.face, (u0 + u1) / 2, (v0 + v1) / 2);
  const corners: readonly [Vec3, Vec3, Vec3, Vec3] = [
    directionForFaceUv(key.face, u0, v0),
    directionForFaceUv(key.face, u1, v0),
    directionForFaceUv(key.face, u0, v1),
    directionForFaceUv(key.face, u1, v1),
  ];
  let cosMin = 1;
  for (const c of corners) cosMin = Math.min(cosMin, dot(centre, c));
  const angularRadius = Math.acos(clamp(cosMin, -1, 1));
  return {
    face: key.face,
    level: key.level,
    u0,
    u1,
    v0,
    v1,
    centre,
    corners,
    angularRadius,
    centreM: scale(centre, radiusM),
    boundRadiusM: 2 * radiusM * Math.sin(angularRadius / 2),
  };
}

// A face edge is crossed by probing a direction just outside it and asking the
// sphere which face that is. The equal-angle projection makes the probe exact:
// one face's angle a = pi/4 + d is the neighbour's pi/4 - d, so the probe lands
// in the adjoining tile whatever the two faces' axis orientations do.
const EDGE_PROBE = 1e-7;

/** The four edge neighbours, in order: -u, +u, -v, +v in this tile's own grid. */
export function neighboursOf(key: TileKey): readonly [TileKey, TileKey, TileKey, TileKey] {
  return [
    neighbourOf(key, -1, 0),
    neighbourOf(key, 1, 0),
    neighbourOf(key, 0, -1),
    neighbourOf(key, 0, 1),
  ];
}

function neighbourOf(key: TileKey, du: number, dv: number): TileKey {
  const n = tilesAcross(key.level);
  const nx = key.x + du;
  const ny = key.y + dv;
  if (nx >= 0 && nx < n && ny >= 0 && ny < n) {
    return { body: key.body, face: key.face, level: key.level, x: nx, y: ny };
  }
  const u = nx < 0 ? -EDGE_PROBE : nx >= n ? 1 + EDGE_PROBE : (key.x + 0.5) / n;
  const v = ny < 0 ? -EDGE_PROBE : ny >= n ? 1 + EDGE_PROBE : (key.y + 0.5) / n;
  return tileForDirection(key.body, directionForFaceUv(key.face, u, v), key.level);
}

// -----------------------------------------------------------------------------
// The runtime
// -----------------------------------------------------------------------------

/**
 * Honesty as data, per tile: the same provenance the plot meta.json already
 * carries (tools/dtm/bake_region.py), promoted into the tile.
 */
export interface TileProvenance {
  readonly instrument: string;
  readonly rights: string;
  /** The source product's own URL, not the tile's. */
  readonly url: string;
  /** How much of this tile was measured rather than inferred or synthesized, 0..1. */
  readonly measured_fraction: number;
  readonly product_id?: string;
  readonly data_set_id?: string;
  readonly note?: string;
  /** The source product's native posting, metres. */
  readonly native_posting_m?: number;
  /** This level's oversample ratio against that posting. */
  readonly oversample?: number;
}

/** Decoded albedo, in whatever form the decode chose to hand the renderer. */
export interface TileAlbedo {
  readonly width: number;
  readonly height: number;
  readonly source: ImageBitmap | Uint8Array;
}

/**
 * A tile's claim state, `TILE_CLAIM_TEXELS`-square R8: 255 claimed, 0 not.
 *
 * `res` is whichever rung the overlay had selected at THIS tile's decode and
 * stays there, the way height and albedo carry whichever product version was
 * current at theirs. The claimed SET is not fixed like that: `cells` records
 * which cell each texel fell in, so `retintClaimTexture` refills `data` in
 * place whenever `claims.ts`'s revision moves and a plot staked under a drawn
 * tile tints it with no reload (`docs/ATLAS-TERRAIN.md`, "Claims").
 */
export interface TileClaims {
  readonly res: number;
  readonly data: Uint8Array<ArrayBuffer>;
  /** Per texel: 0 where no cell resolved, otherwise 1 + the `cellIds` index. */
  readonly cells: Uint16Array<ArrayBuffer>;
  /** The distinct cells this tile's texels fell in, at most one per texel. */
  readonly cellIds: readonly string[];
}

export interface LoadedTile {
  readonly key: TileKey;
  readonly level: number;
  readonly bounds: TileBounds;
  /**
   * Metres above the datum, row-major, `(TILE_HEIGHT_SAMPLES + 2)^2` with the
   * one-sample apron. Null on a body whose height pyramid stops above this level.
   */
  readonly height: Float32Array | null;
  readonly albedo: TileAlbedo | null;
  /**
   * Invented imagery over this tile's UNMEASURED texels, alpha 1 where it has
   * any and 0 everywhere the albedo role measured.
   *
   * Null whenever the ARTIFICIAL FILL layer was off at this tile's fetch, and
   * null on a tile the fill has nothing for - the two are the same absence to
   * everything downstream, which is why neither reaches the provenance.
   */
  readonly artificial: TileAlbedo | null;
  /** Null where no claim state was available at decode time (h3-js not yet
   *  loaded, or nothing anywhere is claimed). */
  readonly claims: TileClaims | null;
  readonly provenance: TileProvenance;
  /** What this tile costs against the memory budget. */
  readonly bytes: number;
  /** Frees what the decode allocated. Called on an `evict` by the consumer, and by `TileTree.dispose`. */
  dispose(): void;
}

/**
 * A tile's claim texture, texel by texel: this tile's own ground direction,
 * turned into an H3 cell at `res` and tested against `claimed`.
 *
 * `cellForLatLon` is injected rather than this module importing h3-js, so
 * `tiletree-check.mjs` keeps loading the whole module under node with no
 * lazy-h3 state of its own to stub.
 */
export function buildClaimTexture(
  key: TileKey,
  radiusM: number,
  res: number,
  claimed: ReadonlySet<string>,
  cellForLatLon: (latDeg: number, lonDeg: number, res: number) => string | null,
): TileClaims {
  const b = boundsOf(key, radiusM);
  const n = TILE_CLAIM_TEXELS;
  const data = new Uint8Array(n * n);
  const cells = new Uint16Array(n * n);
  const index = new Map<string, number>();
  const cellIds: string[] = [];
  for (let j = 0; j < n; j++) {
    const v = b.v0 + ((b.v1 - b.v0) * (j + 0.5)) / n;
    for (let i = 0; i < n; i++) {
      const u = b.u0 + ((b.u1 - b.u0) * (i + 0.5)) / n;
      const { latRad, lonRad } = latLonForDirection(directionForFaceUv(key.face, u, v));
      const cell = cellForLatLon((latRad * 180) / Math.PI, (lonRad * 180) / Math.PI, res);
      if (cell === null) continue;
      let k = index.get(cell);
      if (k === undefined) {
        k = cellIds.length;
        cellIds.push(cell);
        index.set(cell, k);
      }
      // 1-based, so 0 stays "no cell" and can never be claimed.
      cells[j * n + i] = k + 1;
      if (claimed.has(cell)) data[j * n + i] = 255;
    }
  }
  return { res, data, cells, cellIds };
}

/**
 * Refill a claim texture's bytes from a moved claimed set, in place.
 *
 * The texel-to-cell decision is the expensive half (4,096 h3 lookups a tile,
 * which is why the decode does it in the worker) and it does not move when a
 * claim does: only which of `cellIds` are held changes. Returns whether any
 * byte actually moved, so a caller can skip the GPU upload and the redraw.
 */
export function retintClaimTexture(claims: TileClaims, claimed: ReadonlySet<string>): boolean {
  const { data, cells, cellIds } = claims;
  const mask = new Uint8Array(cellIds.length + 1);
  for (let k = 0; k < cellIds.length; k++) mask[k + 1] = claimed.has(cellIds[k]!) ? 255 : 0;
  let changed = false;
  for (let t = 0; t < data.length; t++) {
    const want = mask[cells[t]!]!;
    if (data[t] !== want) {
      data[t] = want;
      changed = true;
    }
  }
  return changed;
}

/** What a claim texture costs the tile budget: its bytes, its index and its ids. */
export function claimBytes(claims: TileClaims): number {
  // 32 bytes an id is V8's flat-string header plus a 15-character H3 index,
  // near enough for a budget that already rounds a bitmap to width x height x 4.
  return claims.data.byteLength + claims.cells.byteLength + claims.cellIds.length * 32;
}

/**
 * The claim byte a tile carries under a body-fixed direction, or null off it.
 *
 * `--hex --claims`'s restake bar reads this beside the frame: a pixel that
 * stepped says something washed, and this says it was THIS tile's own texture
 * and not the globe underlay showing through beside it.
 */
export function claimAtDirection(
  claims: TileClaims,
  bounds: TileBounds,
  dir: Vec3,
): number | null {
  const uv = faceUvForDirection(dir);
  if (uv.face !== bounds.face) return null;
  const n = TILE_CLAIM_TEXELS;
  const i = Math.floor(((uv.u - bounds.u0) / (bounds.u1 - bounds.u0)) * n);
  const j = Math.floor(((uv.v - bounds.v0) / (bounds.v1 - bounds.v0)) * n);
  if (i < 0 || i >= n || j < 0 || j >= n) return null;
  return claims.data[j * n + i] ?? null;
}

/**
 * Where a tile's albedo comes from when its own level has none.
 *
 * The albedo pyramid stops shallower than the height pyramid on every body in
 * 03-terrain-pyramid.md's table - Mars height reaches L7 and its Viking albedo
 * L6, and a detail region's leaves are height only - so a tile below the albedo
 * floor draws with the nearest resident ancestor's texture and the part of it
 * that this tile covers. Every ancestor of a drawn tile is resident by
 * construction: the traversal only descends through tiles it already holds.
 *
 * The rectangle is in the PROVIDER's own equal-angle face box, u and v each
 * 0..1 across it, so `uFrom = offsetU + scale * uLocal`. Which way a texture's
 * rows run is the renderer's convention and is applied there, not here.
 */
export interface TileAlbedoRef {
  readonly from: LoadedTile;
  readonly offsetU: number;
  readonly offsetV: number;
  /** One rectangle side, and the same for both: a sub-tile is square. */
  readonly scale: number;
  /** Levels between the drawn tile and its provider; never 0. */
  readonly levelsUp: number;
}

/**
 * Where a tile's HEIGHT comes from when its own level has none.
 *
 * Mercury, Venus and Phobos bake imagery one level deeper than relief, so every
 * leaf under the height floor arrives with a null field and would otherwise be
 * drawn on the datum sphere - the ground flattening exactly where the eye is
 * closest to it. Same rectangle and the same residency guarantee as the albedo
 * ref, so the ancestor's relief is reachable without a fetch of its own.
 */
export type TileHeightRef = TileAlbedoRef;

/** Per drawn tile, what the renderer needs that the tile itself does not carry. */
export interface TileDrawState {
  /** The inherited albedo, or null when the tile has its own. */
  readonly albedo: TileAlbedoRef | null;
  /** The inherited height field, or null when the tile has its own. */
  readonly height: TileHeightRef | null;
  /**
   * The inherited artificial fill, or null when the tile has its own or the
   * layer is off.
   *
   * Its OWN chain, not the albedo's: the fill's floor is the shallower of the
   * two (pluto fills to L3 against an albedo floor of L4), so a tile carrying
   * its own measured imagery is already borrowing invented ground from an
   * ancestor, and the two rectangles differ from that level down.
   */
  readonly artificial: TileAlbedoRef | null;
  /**
   * How far this tile is between its parent's shape and its own, 0..1.
   *
   * A drawn tile's pixels per sample live in (splitAt/2, splitAt]: below that it
   * merges into its parent, above it splits. 0 is the instant it appeared out of
   * a split, where drawing it on the parent's vertex lattice makes the split
   * invisible; 1 is the instant it splits itself.
   */
  readonly morph: number;
}

/**
 * A detail region: levels that exist only inside named subtrees.
 *
 * The global pyramid stops at `maxLevel` because that is where the global
 * product stops. A site bake carries on below it over a few square kilometres,
 * and enters the same quadtree as leaves under whichever tiles cover the site
 * (03-terrain-pyramid.md, "What goes global and what stays a region"). The tree
 * descends past `maxLevel` only inside a root's subtree, and the
 * whole-level-or-nothing rule is unchanged there - which is why the bake closes
 * every descendant of a root down to `floorLevel`, parent-filling the ones no
 * instrument measured.
 */
export interface TileExtension {
  readonly id: string;
  /** Deepest level inside this region. */
  readonly floorLevel: number;
  /** Tiles whose whole subtree is closed down to `floorLevel`. */
  readonly roots: readonly TileKey[];
}

/** What a fetch hands back: still bytes, not yet a field or a texture. */
export interface FetchedTile {
  readonly key: TileKey;
  readonly height: ArrayBuffer | null;
  readonly albedo: ArrayBuffer | null;
  /** The artificial fill's bytes, fetched only while its layer is on. */
  readonly artificial?: ArrayBuffer | null;
  readonly provenance: TileProvenance;
}

export type TileFetch = (key: TileKey, signal: AbortSignal) => Promise<FetchedTile>;
export type TileDecode = (fetched: FetchedTile, signal: AbortSignal) => Promise<LoadedTile>;

export interface View {
  /** Camera position in the body-fixed frame, metres from the body centre. */
  readonly cameraM: Vec3;
  /** Vertical field of view, degrees. */
  readonly fovDeg: number;
  /** Viewport height in device pixels, renderScale included. */
  readonly viewportPx: number;
  /**
   * Metres above the datum. Taken from the focus camera rather than derived
   * from `cameraM`, so the horizon and the band cannot disagree on a datum that
   * is not a sphere.
   */
  readonly altitudeM: number;
  /**
   * Six frustum planes in the body-fixed frame, packed `nx, ny, nz, c` each in
   * `FRUSTUM_ORDER`, a point inside where `n . p + c >= 0`. `frustumPlanes`
   * builds them; the camera's forward axis is what it is built from and lives
   * in these rather than beside them.
   *
   * Optional, and the traversal culls by the horizon alone without it: every
   * synthetic view an instrument builds, and the traversal as it was.
   */
  readonly frustumM?: readonly number[];
}

/** `frustumM`'s plane order. */
export const FRUSTUM_ORDER = ['left', 'right', 'bottom', 'top', 'near', 'far'] as const;
/** Numbers in `frustumM`: six planes of four. */
export const FRUSTUM_FLOATS = 24;

/**
 * The six planes of a symmetric perspective camera, body-fixed, into `out`.
 *
 * `tanX`/`tanY` are the half-angle tangents, which come off a three.js
 * projection matrix as `1 / e[0]` and `1 / e[5]` and so carry a borrowed
 * projection exactly. The far plane is placed from `farM` rather than read back
 * out of that matrix: at the Atlas's 0.0005 m near against a 2e14 m far the
 * depth terms cancel to three significant figures and the extracted far plane
 * lands three orders of magnitude short of the real one.
 *
 * Writes 24 numbers and allocates nothing, so the caller keeps one array.
 */
export function frustumPlanes(
  eyeM: Vec3,
  forwardM: Vec3,
  upM: Vec3,
  tanX: number,
  tanY: number,
  farM: number,
  out: number[],
): void {
  const fl = Math.hypot(forwardM[0], forwardM[1], forwardM[2]) || 1;
  const fx = forwardM[0] / fl;
  const fy = forwardM[1] / fl;
  const fz = forwardM[2] / fl;
  let rx = fy * upM[2] - fz * upM[1];
  let ry = fz * upM[0] - fx * upM[2];
  let rz = fx * upM[1] - fy * upM[0];
  const rl = Math.hypot(rx, ry, rz) || 1;
  rx /= rl;
  ry /= rl;
  rz /= rl;
  const ux = ry * fz - rz * fy;
  const uy = rz * fx - rx * fz;
  const uz = rx * fy - ry * fx;
  putPlane(out, 0, rx + tanX * fx, ry + tanX * fy, rz + tanX * fz, eyeM, 0);
  putPlane(out, 4, -rx + tanX * fx, -ry + tanX * fy, -rz + tanX * fz, eyeM, 0);
  putPlane(out, 8, ux + tanY * fx, uy + tanY * fy, uz + tanY * fz, eyeM, 0);
  putPlane(out, 12, -ux + tanY * fx, -uy + tanY * fy, -uz + tanY * fz, eyeM, 0);
  putPlane(out, 16, fx, fy, fz, eyeM, 0);
  putPlane(out, 20, -fx, -fy, -fz, eyeM, farM);
}

/** One plane through `eyeM`, or set back `aheadM` against its own normal, so the eye is `aheadM` inside it. */
function putPlane(
  out: number[], k: number,
  nx: number, ny: number, nz: number,
  eyeM: Vec3, aheadM: number,
): void {
  const l = Math.hypot(nx, ny, nz) || 1;
  const x = nx / l;
  const y = ny / l;
  const z = nz / l;
  out[k] = x;
  out[k + 1] = y;
  out[k + 2] = z;
  out[k + 3] = aheadM - (x * eyeM[0] + y * eyeM[1] + z * eyeM[2]);
}

/**
 * Pixels per height sample above which a tile splits.
 *
 * The doc's threshold, and its consequence - a 256-sample tile reaching about
 * 384 pixels on screen requests its children - fix the direction: a tile is
 * under-resolved when it is being magnified past this, so it splits ABOVE the
 * threshold, not below it as 03-terrain-pyramid.md's sentence reads.
 */
export const SPLIT_PIXELS_PER_SAMPLE = 1.5;
/** The viewport the threshold was tuned on. */
export const REFERENCE_VIEWPORT_PX = 900;
export const REFERENCE_FOV_DEG = 45;
export const DEFAULT_MAX_IN_FLIGHT = 24;
/**
 * What the cap falls to while `setFetchHold` is on.
 *
 * Not zero: the ground under a descending camera still has to resolve, and a
 * stream stopped dead is a level boundary the player flies through. Two leaves
 * the worst-resolved tiles on screen moving while the read that holds the
 * queue - the claim's own, in src/atlas.ts - takes the rest of the pipe.
 */
export const HOLD_MAX_IN_FLIGHT = 2;
export const DEFAULT_BUDGET_BYTES = 800 * 1024 * 1024;
const DEFAULT_MAX_ATTEMPTS = 3;
/**
 * How long a request may stay on the wire, per slot the wire had when it went
 * out: 96 s at the default cap and 8 s under the two-slot hold.
 *
 * Per slot because a busy wire is a slow one, and a timeout that cut a merely
 * slow link would never let it finish anything. 4 s is the 250 KB a tile costs
 * across its roles (`--ship`'s own count) at half a megabit a slot.
 */
const FETCH_TIMEOUT_PER_SLOT_MS = 4000;
const MIN_DISTANCE_M = 1e-3;
/** How many of the smallest-margin drawn tiles `ringHole` drops, every request. */
const RING_HOLE_TILES = 1;
/**
 * Altitude in body radii above which `frustumM` is ignored and nothing is culled.
 *
 * camera.ts's ORBIT edge, which is where the whole disc first fits the frame:
 * from there up the globe regimes draw every tile of their level and the
 * frustum has nothing to reject, so the band that draws the whole body is left
 * on the traversal it always had rather than gated on a borrowed projection.
 */
const FRUSTUM_CULL_CEILING_RADII = 1.613;
/**
 * Tile widths of ground the rejection keeps beyond the frame edge.
 *
 * A tile the frustum rejects is never fetched, so the level its ground is
 * drawn at stops where the rejection bit. Widening the frame walks that
 * boundary inward and the ground arriving has only a coarse ancestor to draw
 * it, which is what a pull-back from LOCAL to ORBIT reads as squares
 * sharpening one after another. The margin is the tile's own cap rather than a
 * distance or an angle, so it is one ring of ground at every level: at the
 * coarse levels a pull-back climbs to, one tile is hundreds of kilometres of
 * lead, and the ring costs each level a perimeter rather than an area. An
 * angular margin wide enough to do the same job at those levels is several
 * frames wide at the fine ones, where the tiles are.
 *
 * The guarantee the rejection carries is untouched: the cap only grows, so a
 * tile with any part in frame is still never rejected.
 */
const FRUSTUM_MARGIN_TILES = 1;
/**
 * How much a split's priority rises per sibling already resident.
 *
 * Four children are one unit of work - none of them can be drawn until all
 * four have landed - so a quad three quarters of the way there is worth more
 * than a fresh one at the same screen-space error. One level of refinement is
 * a factor of two apart, so three steps of this can never reorder a level
 * against the one above it.
 */
const QUAD_LANDED_BONUS = 0.01;
/**
 * Levels the LRU never takes.
 *
 * The coarse skin is what a traversal draws when nothing finer has arrived,
 * and it is 30 tiles for the whole body against a budget of hundreds. Evicting
 * one costs a fetch that the ground on screen is waiting behind, and drops the
 * fallback from coarse to nothing.
 */
const COARSE_KEEP_LEVEL = 1;
/**
 * `request()` passes a timed-out tile may sort behind fresh work before it
 * takes the next free slot regardless of priority.
 *
 * Unbounded, a descent whose wanted set stays over the cap can starve it past
 * the point the camera ever settles: a sweep with nothing else contending for
 * the wire still reads gaps of several dozen passes between a timeout and the
 * cap dipping low enough on its own to admit it. 30 is about a second at the
 * 30 fps `tiletree-check`'s own slow-link scenarios model (`SLOW_LINK.fps`) -
 * long enough that ordinary admission usually clears it first, short enough
 * that a continuously busy descent cannot leave one tile's ground coarse for
 * much more than a second.
 */
const STALLED_BOUNDED_WAIT_PASSES = 30;

/**
 * Pixels one height sample covers at level L seen from `distanceM`.
 *
 * This is `metresPerPixel` from src/atlas/camera.ts with the distance to the
 * tile in place of the altitude, so the reference 1600x900 viewport at 45
 * degrees gives the doc's `1086 * GSD(L) / d`.
 */
export function pixelsPerSample(
  level: number,
  distanceM: number,
  radiusM: number,
  fovDeg: number,
  viewportPx: number,
): number {
  const mpp = (2 * distanceM * Math.tan((fovDeg * Math.PI) / 360)) / Math.max(1, viewportPx);
  return gsd(level, radiusM) / mpp;
}

/**
 * The altitude at which one rendered pixel is one albedo cell of level L.
 *
 * On the reference viewport above rather than the live one, so an imagery floor
 * is a property of the body's own data: a floor derived from the window would
 * put the same body's descent in a different band on two screens.
 */
export function imageryFloorAltitudeM(level: number, radiusM: number): number {
  return (albedoGsd(level, radiusM) * REFERENCE_VIEWPORT_PX)
    / (2 * Math.tan((REFERENCE_FOV_DEG * Math.PI) / 360));
}

export interface TileTreeConfig {
  readonly body: string;
  /** Datum radius, metres. The grid and every GSD follow from it alone. */
  readonly radiusM: number;
  /** Deepest level that exists for this body. */
  readonly maxLevel: number;
  /** Shallowest level, always the root set. Default 0. */
  readonly minLevel?: number;
  /** Texture and buffer memory the resident set may hold. */
  readonly budgetBytes?: number;
  readonly maxInFlight?: number;
  readonly splitPixelsPerSample?: number;
  /**
   * Highest terrain above the datum, metres. Over-includes at the horizon on
   * purpose: culling a peak that is visible leaves a hole, drawing one that is
   * not costs a tile. Default one percent of the radius, which is a round
   * body's number: a caller streaming one that is not round passes its own.
   */
  readonly reliefM?: number;
  /**
   * Take the camera's radius from the focus altitude alone, the rule before a
   * body whose ground stands a fifth of its radius off the datum was streamed.
   * `tile-swap-check --coverage --inject legacy-horizon`'s control, nothing else.
   */
  readonly horizonFromEye?: boolean;
  /**
   * Tile widths of ground the frustum rejection keeps beyond the frame edge.
   * Default `FRUSTUM_MARGIN_TILES`; zero is the rejection with no ring at all,
   * which is `tiletree-check zoomout --inject no-margin`'s control.
   */
  readonly frustumMarginTiles?: number;
  /**
   * Let the relief term drive REFINEMENT as well as visibility, the rule
   * before a descent's request count was measured.
   *
   * `descent-dojo --ship --inject ring-unbudgeted`'s control, nothing else.
   */
  readonly reliefRefines?: boolean;
  /**
   * Drop the one drawn tile whose margin to the split horizon is smallest,
   * every request: a punched hole at the grazing edge of the band, on demand
   * rather than by a fault, to prove a reading can see one.
   *
   * `descent-dojo --ship --inject ring-hole`'s control, and `tiletree-check
   * orphan`'s landing-guard reach case, parameterized directly rather than
   * through `--inject`.
   */
  readonly ringHole?: boolean;
  /**
   * The queue as it was before a descent's abandoned requests were counted: an
   * in-flight request that falls out of the top of the priority order is
   * abandoned whatever it has already downloaded, and holes are ordered by
   * level alone rather than by level and then by distance from the eye.
   *
   * `descent-dojo --ship --inject ring-churn`'s control, nothing else.
   */
  readonly legacyRingQueue?: boolean;
  /**
   * The queue as it was before a split's four children were admitted as one
   * unit: a slot goes to whichever child sorts next, so on a link that cannot
   * keep up the cap spreads across more quads than it can finish and none of
   * them is ever drawn. The coarse skin becomes evictable again with it.
   *
   * `tiletree-check residency --inject loose-quad`'s control, nothing else.
   */
  readonly legacyQuadQueue?: boolean;
  /**
   * Deepest level the LRU may never take. Default `COARSE_KEEP_LEVEL`;
   * negative exempts nothing, which is `tiletree-check lru`'s own ordering
   * case, where the victims ARE the coarse skin.
   */
  readonly coarseKeepLevel?: number;
  /** Fetch attempts before a tile is given up on. Default 3. A timeout is not one. */
  readonly maxAttempts?: number;
  /**
   * A request's timeout for every slot the wire had when it went out. Default
   * `FETCH_TIMEOUT_PER_SLOT_MS`; zero is no timeout, which is `tiletree-check
   * hang --inject no-timeout`'s control.
   */
  readonly fetchTimeoutPerSlotMs?: number;
  /**
   * The LRU touch as it was: a tile touched before the subtree the pass
   * reaches under it, so the LRU holds a parent older than its own children.
   *
   * `tiletree-check orphan --inject touch-first`'s control, and half of
   * `--inject pre-order`'s; nothing else.
   */
  readonly legacyTouchOrder?: boolean;
  /**
   * The eviction as it was: a tile taken oldest first whatever is resident
   * under it, which strands a subtree the traversal can no longer reach.
   *
   * `tiletree-check orphan --inject evict-parents`'s control, and the other
   * half of `--inject pre-order`'s; nothing else.
   */
  readonly legacyEvictParents?: boolean;
  /**
   * The fetch deadline as it was before it was scoped to the fetch alone: the
   * timer set before `fetchTile` is never cleared for `decode`, so a tile
   * whose bytes arrived in time is still cut if the decode pool is busy past
   * the same deadline.
   *
   * `tiletree-check hang --inject slow-decode`'s control, nothing else.
   */
  readonly legacyFetchDeadline?: boolean;
  /**
   * A timeout reported through `onError` as though it were a failure, the way
   * every other rejection is: the key doubles (`onError`'s own caller in
   * src/atlas.ts already prefixes it) and the message lingers on the HUD past
   * the retry that fills the tile, since nothing ever clears it.
   *
   * `tiletree-check hang --inject timeout-error`'s control, nothing else.
   */
  readonly legacyTimeoutError?: boolean;
  /**
   * A landed tile kept resident even when its immediate parent is gone: the
   * traversal only reaches a tile through a resident parent, so a child whose
   * fetch outlives its parent's eviction would otherwise sit resident and
   * undrawn until the LRU happens to take it back. Defense in depth rather
   * than a reachable path: `visit()` draws a splitting tile's own key in the
   * same branch that wants its unresolved children, so a child never stays
   * wanted past the pass its parent stops being drawn (and so protected from
   * eviction) - `tiletree-check orphan`'s delayed-source case measured this
   * directly and never saw it fire there. `--inject late-landing` reaches it
   * by breaking the coupling on purpose with `ringHole`.
   */
  readonly legacyLateLanding?: boolean;
  /**
   * A timed-out tile sorts behind fresh work forever rather than taking the
   * next free slot after `STALLED_BOUNDED_WAIT_PASSES` passes.
   *
   * `tiletree-check residency --inject unbounded-stall`'s control, nothing else.
   */
  readonly legacyUnboundedStall?: boolean;
  /**
   * `request()` passes a stalled id waits before the bounded wait admits it.
   * Default `STALLED_BOUNDED_WAIT_PASSES`; `tiletree-check residency` leaves
   * it at the shipped value and reads the wait it produces rather than
   * lowering it for the check.
   */
  readonly stalledBoundedWaitPasses?: number;
  /** Detail regions the traversal may descend into below `maxLevel`. */
  readonly extensions?: readonly TileExtension[];
  readonly fetch: TileFetch;
  readonly decode: TileDecode;
  readonly onError?: (key: TileKey, error: unknown) => void;
}

export interface TileTreeUpdate {
  /** In the draw set now and not last pass: build geometry for these. */
  readonly mount: readonly LoadedTile[];
  /** In the draw set now and last pass: nothing to do. */
  readonly keep: readonly LoadedTile[];
  /** Out of the draw set, still cached: hide these. */
  readonly unmount: readonly LoadedTile[];
  /** Out of the cache: unmount, then call `dispose` on each. */
  readonly evict: readonly LoadedTile[];
  /**
   * One entry per tile in the draw set, keyed by `formatTileKey`.
   *
   * Beside `mount`/`keep` rather than inside them so a consumer that draws a
   * tile flat can ignore it, and so a KEPT tile can be handed a state that
   * changed - the morph does, every pass.
   */
  readonly draw: ReadonlyMap<string, TileDrawState>;
}

/** Tile ids, keyed by `formatTileKey`, that `request` must not evict. A `Set` or a `Map` keyed by id. */
export interface TileHeld {
  has(id: string): boolean;
}

export interface TileTreeStats {
  readonly resident: number;
  readonly residentBytes: number;
  readonly inFlight: number;
  /** Wanted, not resident, and not in flight because the cap is full. */
  readonly queued: number;
  readonly fetched: number;
  readonly aborted: number;
  /**
   * Requests the ORDER abandoned: still wanted, part-downloaded, given up on.
   *
   * Zero unless `legacyRingQueue` is on. A tile the traversal stopped wanting,
   * and one a hold clamped off the pipe, are aborts with their own reasons and
   * are not counted here.
   */
  readonly superseded: number;
  /** Requests that hit their timeout and went back to the queue. */
  readonly timedOut: number;
  readonly failed: number;
  /** The resident set is over budget because everything left is on screen. */
  readonly overBudget: boolean;
  /**
   * Every tile the last traversal asked for is resident.
   *
   * False while one is still coming AND while one has failed past its attempt
   * cap, since a permanent hole is a hole.
   */
  readonly complete: boolean;
  /** Off screen but held anyway, because a drawn tile inherits its albedo. */
  readonly pinned: number;
  /** Whether the cap is presently clamped for a higher-priority read. */
  readonly fetchHold: boolean;
  /** The deepest level the clamp admits, or Infinity where it admits every one. */
  readonly holdMaxLevel: number;
  /** Traversals that ran under that clamp - a control's own readback. */
  readonly heldPasses: number;
}

export interface TileTree {
  /**
   * Traverse for this view, start and cancel fetches, and report the draw-set delta.
   *
   * `held` names tiles the caller is still drawing outside the draw set, such
   * as a mesh kept on screen past its unmount; the LRU passes over them the
   * way it passes over the draw set.
   */
  request(view: View, held?: TileHeld): TileTreeUpdate;
  /**
   * Clamp the in-flight cap to `HOLD_MAX_IN_FLIGHT` for a read that outranks
   * the stream. The tree has no notion of what that read is; the caller holds
   * and releases it, and a caller that forgets to release starves the ground.
   *
   * `maxLevel` clamps by DEPTH as well: while the hold is on, nothing deeper is
   * started, so the coarse picture under the camera keeps resolving and the
   * levels the higher-priority read is about to replace wait for it. Omitted,
   * every level is admitted and the cap alone does the holding.
   */
  setFetchHold(holding: boolean, maxLevel?: number): void;
  stats(): TileTreeStats;
  /** Resident tile ids, least recently touched first: `tiletree-check orphan`'s touch-order read. */
  residentOrder(): string[];
  /** Aborts every request in flight and disposes every resident tile. */
  dispose(): void;
}

interface InFlight {
  readonly key: TileKey;
  readonly controller: AbortController;
  readonly timer: ReturnType<typeof setTimeout> | null;
}

interface Wanted {
  readonly id: string;
  readonly key: TileKey;
  /**
   * The parent whose split this request is one quarter of, or null for a hole.
   *
   * A hole is drawn the moment it lands; a split's four children are one unit
   * of work and none of them is drawn until all four are resident, which is
   * what `admitStarts` gives the wire a quad at a time.
   */
  readonly group: string | null;
  priority: number;
}

/** A priority-ordered want list cut into sibling groups, order preserved. */
function siblingGroups(order: readonly Wanted[]): Wanted[][] {
  const groups: Wanted[][] = [];
  const byGroup = new Map<string, Wanted[]>();
  for (const t of order) {
    const held = t.group === null ? undefined : byGroup.get(t.group);
    if (held) {
      held.push(t);
      continue;
    }
    const fresh = [t];
    if (t.group !== null) byGroup.set(t.group, fresh);
    groups.push(fresh);
  }
  return groups;
}

// A missing tile is a hole in the picture and outranks any refinement; the
// coarser hole is the bigger hole, and within one level the hole nearest the
// sub-camera point is filled first. Refinement priority is how far past the
// split threshold the parent already is, so the worst-resolved ground on screen
// is fetched first.
const HOLE_PRIORITY = 1e6;

const HAS_ALBEDO = (tile: LoadedTile): boolean => tile.albedo !== null;
const HAS_HEIGHT = (tile: LoadedTile): boolean => tile.height !== null;
// `?? null` and not `!== null`: the node-side stubs in tools/preview build a
// LoadedTile without the field at all, and an undefined fill is no fill.
const HAS_ARTIFICIAL = (tile: LoadedTile): boolean => (tile.artificial ?? null) !== null;

export function createTileTree(config: TileTreeConfig): TileTree {
  const { body, radiusM, maxLevel, fetch: fetchTile, decode } = config;
  const minLevel = config.minLevel ?? 0;
  const budgetBytes = config.budgetBytes ?? DEFAULT_BUDGET_BYTES;
  const maxInFlight = config.maxInFlight ?? DEFAULT_MAX_IN_FLIGHT;
  const splitAt = config.splitPixelsPerSample ?? SPLIT_PIXELS_PER_SAMPLE;
  const reliefM = config.reliefM ?? radiusM * 0.01;
  const marginTiles = Math.max(0, config.frustumMarginTiles ?? FRUSTUM_MARGIN_TILES);
  const horizonFromEye = config.horizonFromEye ?? true;
  const reliefRefines = config.reliefRefines ?? false;
  const ringHole = config.ringHole ?? false;
  const legacyRingQueue = config.legacyRingQueue ?? false;
  const legacyQuadQueue = config.legacyQuadQueue ?? false;
  const coarseKeep = legacyQuadQueue ? -1 : config.coarseKeepLevel ?? COARSE_KEEP_LEVEL;
  const maxAttempts = config.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const timeoutPerSlotMs = Math.max(0, config.fetchTimeoutPerSlotMs ?? FETCH_TIMEOUT_PER_SLOT_MS);
  const legacyTouchOrder = config.legacyTouchOrder ?? false;
  const legacyEvictParents = config.legacyEvictParents ?? false;
  const legacyFetchDeadline = config.legacyFetchDeadline ?? false;
  const legacyTimeoutError = config.legacyTimeoutError ?? false;
  const legacyLateLanding = config.legacyLateLanding ?? false;
  const legacyUnboundedStall = config.legacyUnboundedStall ?? false;
  const stalledBoundedWaitPasses = config.stalledBoundedWaitPasses ?? STALLED_BOUNDED_WAIT_PASSES;
  // Which subtrees carry on below `maxLevel`, indexed by the root's own key so
  // the traversal's test is one map lookup at one level rather than a scan.
  const extFloor = new Map<string, number>();
  const extRootLevels = new Set<number>();
  for (const ext of config.extensions ?? []) {
    for (const root of ext.roots) {
      const id = formatTileKey(root);
      extFloor.set(id, Math.max(extFloor.get(id) ?? 0, ext.floorLevel));
      extRootLevels.add(root.level);
    }
  }

  // Insertion order is LRU order: every tile the traversal draws is re-inserted.
  const resident = new Map<string, LoadedTile>();
  const inFlight = new Map<string, InFlight>();
  const failures = new Map<string, number>();
  // Timed out and not landed since: retried behind everything else waiting,
  // so a request that hangs again cannot hold the wire ahead of new quads.
  const stalled = new Set<string>();
  // Consecutive passes a stalled id has been wanted and NOT handed a slot;
  // reset once it is (or once it lands, since `stalled` drops it then too).
  const stalledWaitPasses = new Map<string, number>();
  let drawnIds = new Set<string>();
  let residentBytes = 0;
  let fetched = 0;
  let aborted = 0;
  let superseded = 0;
  let timedOut = 0;
  let failed = 0;
  let queued = 0;
  let fetchHold = false;
  let holdMaxLevel = Infinity;
  let heldPasses = 0;
  let overBudget = false;
  let complete = true;
  let pinnedCount = 0;
  let disposed = false;

  function abort(id: string, req: InFlight, reason: string): void {
    if (req.timer !== null) clearTimeout(req.timer);
    req.controller.abort(new Error(`tile ${id}: ${reason}`));
    inFlight.delete(id);
    aborted++;
    if (reason === 'superseded') superseded++;
  }

  function start(key: TileKey, id: string, cap: number): void {
    const controller = new AbortController();
    let timer = timeoutPerSlotMs > 0
      ? setTimeout(() => {
        const req = inFlight.get(id);
        if (req?.controller !== controller) return;
        abort(id, req, 'timed out');
        timedOut++;
        // Not a failed attempt: three on a link that is only slow would give
        // the tile up and leave its quad at the parent for good.
        stalled.add(id);
        // Not a reported failure either: it goes back to the queue and a
        // retry commonly fills it, so surfacing it through onError the way a
        // real rejection is would leave a message on the HUD nothing clears.
        if (legacyTimeoutError) config.onError?.(key, new Error(`tile ${id}: timed out`));
      }, timeoutPerSlotMs * cap)
      : null;
    inFlight.set(id, { key, controller, timer });
    void (async () => {
      try {
        const raw = await fetchTile(key, controller.signal);
        if (controller.signal.aborted || disposed) return;
        // The deadline covers the fetch alone: decode has its own pool and can
        // legitimately queue past it without the bytes that already arrived
        // being thrown away.
        if (!legacyFetchDeadline && timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
        const tile = await decode(raw, controller.signal);
        if (controller.signal.aborted || disposed) {
          tile.dispose();
          return;
        }
        failures.delete(id);
        stalled.delete(id);
        stalledWaitPasses.delete(id);
        fetched++;
        // The traversal only reaches a tile through a resident parent: one
        // whose fetch outlives its parent's eviction cannot be reached even
        // though it landed, so keeping it resident would just strand it until
        // the LRU happens to take it back.
        const parent = parentOf(key);
        if (!legacyLateLanding && parent && !resident.has(formatTileKey(parent))) {
          tile.dispose();
          return;
        }
        resident.set(id, tile);
        residentBytes += tile.bytes;
      } catch (e) {
        if (controller.signal.aborted || disposed) return;
        failures.set(id, (failures.get(id) ?? 0) + 1);
        failed++;
        track('exception', { kind: 'tile', detail: e instanceof Error ? e.message : String(e), body_id: config.body, fatal: false });
        config.onError?.(key, e);
      } finally {
        if (timer !== null) clearTimeout(timer);
        if (inFlight.get(id)?.controller === controller) inFlight.delete(id);
      }
    })();
  }

  function touch(id: string, tile: LoadedTile): void {
    resident.delete(id);
    resident.set(id, tile);
  }

  /**
   * Who holds a slot this pass.
   *
   * A request already on the wire that the traversal still wants takes its
   * slot back first, in priority order, because the bytes it holds are spent
   * whether it finishes or not. New starts take what is left, and take it a
   * QUAD at a time: a split's four children cannot be drawn until all four are
   * resident, so a cap spread over more quads than the link can finish in a
   * move draws none of them and the ground stays at the ancestor it had. The
   * first group that does not fit STOPS the fill rather than being skipped, so
   * the worst-resolved ground on screen cannot be starved by smaller groups
   * behind it; with nothing on the wire one partial group still goes out, so a
   * cap under four - which is what `setFetchHold` clamps to - never stalls.
   * Keyed on the empty wire, not the group: it cannot fire at a cap of four or
   * more, and one started beside held work leaves two quads part filled.
   */
  function admitStarts(order: readonly Wanted[], cap: number): Wanted[] {
    const holding = order.filter((t) => inFlight.has(t.id));
    const fresh = order.filter((t) => !inFlight.has(t.id));
    if (legacyQuadQueue) return [...holding, ...fresh].slice(0, cap);
    let room = cap - holding.length;
    const picked: Wanted[] = [];
    for (const group of siblingGroups(fresh)) {
      if (room <= 0) break;
      if (group.length <= room) {
        picked.push(...group);
        room -= group.length;
        continue;
      }
      if (holding.length === 0 && picked.length === 0) picked.push(...group.slice(0, room));
      break;
    }
    return [...holding, ...picked].slice(0, cap);
  }

  function request(view: View, held?: TileHeld): TileTreeUpdate {
    if (disposed) return { mount: [], keep: [], unmount: [], evict: [], draw: new Map() };

    const camDir = normalize(view.cameraM);
    // The eye's own radius, and never under the focus altitude over the datum:
    // each understates on its own. The altitude does when the ground the eye
    // stands over is above the datum, which on Phobos is by a fifth of the
    // radius and put the horizon a third of the way in from the limb; the
    // radius does when a pitched eye is out over the horizon rather than up.
    const eyeRadiusM = horizonFromEye ? length(view.cameraM) : 0;
    const camRadiusM = Math.max(
      eyeRadiusM, radiusM + view.altitudeM, radiusM * (1 + 1e-9),
    );
    // A surface point is visible from radius rc while its great-circle angle
    // from the sub-camera point stays under acos(R/rc); relief adds its own arc.
    const eyeHorizonRad = Math.acos(clamp(radiusM / camRadiusM, -1, 1));
    const horizonRad =
      eyeHorizonRad + Math.acos(clamp(radiusM / (radiusM + reliefM), -1, 1));
    // What may be REFINED, which is not what may be drawn. The relief term
    // over-includes on purpose so a peak the eye can see is never culled, and
    // it is a fixed arc: on Mars it is 8 degrees of ground at every altitude,
    // against the eye's own 0.06 degrees at two metres. Splitting inside it
    // spends a whole descent's requests on ground that is a few pixels tall at
    // a grazing angle. The peak is still drawn, at the level its parent
    // reached, so nothing that was visible goes missing.
    const refineRad = reliefRefines ? horizonRad : eyeHorizonRad;
    // The horizon cap is many times wider than the frame at every band below
    // ORBIT, so everything behind the eye reaches `want()` without this.
    const frustum = view.frustumM !== undefined
      && view.frustumM.length >= FRUSTUM_FLOATS
      && view.altitudeM < radiusM * FRUSTUM_CULL_CEILING_RADII
      ? view.frustumM : null;

    const wanted = new Map<string, Wanted>();
    const draw: LoadedTile[] = [];
    const drawIds = new Set<string>();
    const drawState = new Map<string, TileDrawState>();
    // Ancestors an on-screen tile borrows albedo from. Off screen, so the LRU
    // would take them, and taking one leaves its descendants untextured.
    const pinned = new Set<string>();
    complete = true;

    function want(id: string, key: TileKey, priority: number, group: string | null): void {
      // A tile past its attempt cap is a PERMANENT hole: never requested again,
      // and the tree is not complete while the traversal still wants it.
      complete = false;
      if ((failures.get(id) ?? 0) >= maxAttempts) return;
      const prev = wanted.get(id);
      if (prev) prev.priority = Math.max(prev.priority, priority);
      else wanted.set(id, { id, key, group: stalled.has(id) ? null : group, priority });
    }

    /**
     * The nearest resident ancestor carrying `payload`, and the part of it this
     * tile covers. A key at level L sits at (x mod 2^k) / 2^k of the way across
     * its ancestor k levels up, which is integer arithmetic on the key.
     */
    function inheritFrom(
      key: TileKey,
      payload: (tile: LoadedTile) => boolean,
    ): TileAlbedoRef | null {
      let up = key;
      for (let k = 1; k <= key.level; k++) {
        const parent = parentOf(up);
        if (!parent) return null;
        up = parent;
        const from = resident.get(formatTileKey(parent));
        if (!from) return null;
        if (!payload(from)) continue;
        const d = 2 ** k;
        return {
          from,
          offsetU: (key.x % d) / d,
          offsetV: (key.y % d) / d,
          scale: 1 / d,
          levelsUp: k,
        };
      }
      return null;
    }

    function visit(key: TileKey, deepest: number): void {
      const bounds = boundsOf(key, radiusM);
      const g = Math.acos(clamp(dot(bounds.centre, camDir), -1, 1));
      if (g - bounds.angularRadius > horizonRad) return;
      // The tile's cap, padded by the relief the horizon already over-includes
      // by and by the margin ring, against each plane: wholly outside one of
      // them and neither this tile nor its subtree is fetched, decoded, meshed
      // or drawn. A tile the eye stands inside of survives by construction,
      // since the four side planes and the near plane pass through the eye.
      if (frustum) {
        const capM = bounds.boundRadiusM * (1 + marginTiles) + reliefM;
        for (let p = 0; p < FRUSTUM_FLOATS; p += 4) {
          const d = frustum[p]! * bounds.centreM[0] + frustum[p + 1]! * bounds.centreM[1]
            + frustum[p + 2]! * bounds.centreM[2] + frustum[p + 3]!;
          if (d < -capM) return;
        }
      }

      const id = formatTileKey(key);
      const tile = resident.get(id);
      if (!tile) {
        // The coarser hole first, and within one level the ground under the
        // eye first: `g` is the angular separation from the sub-camera point,
        // and a tile that reaches here is inside the horizon, so `g / PI` is
        // under 1 and can only order a level against itself.
        want(id, key, HOLE_PRIORITY - key.level - (legacyRingQueue ? 0 : g / Math.PI), null);
        return;
      }
      // The LRU touch comes after the subtree, below, so a tile is always newer
      // than what the same pass reached under it; the legacy order touched here.
      if (legacyTouchOrder) touch(id, tile);
      // A detail region's floor replaces the global one from its root down.
      if (extRootLevels.has(key.level)) {
        const floor = extFloor.get(id);
        if (floor !== undefined && floor > deepest) deepest = floor;
      }

      const distanceM = Math.max(
        length(sub(view.cameraM, bounds.centreM)) - bounds.boundRadiusM,
        MIN_DISTANCE_M,
      );
      const pps = pixelsPerSample(key.level, distanceM, radiusM, view.fovDeg, view.viewportPx);
      if (key.level < deepest && pps > splitAt && g - bounds.angularRadius <= refineRad) {
        const kids = childrenOf(key);
        const kidIds = kids.map(formatTileKey);
        // A whole level or nothing: a parent is never drawn with some of its
        // children resolved, so the child set is refined only once all four
        // have arrived.
        if (kidIds.every((kid) => resident.has(kid))) {
          for (const kid of kids) visit(kid, deepest);
          if (!legacyTouchOrder) touch(id, tile);
          return;
        }
        const landed = kidIds.reduce((n, kid) => n + (resident.has(kid) ? 1 : 0), 0);
        const priority = Math.min(
          (pps / splitAt) * (legacyQuadQueue ? 1 : 1 + QUAD_LANDED_BONUS * landed),
          HOLE_PRIORITY - 64,
        );
        for (let i = 0; i < 4; i++) {
          const kidId = kidIds[i]!;
          if (!resident.has(kidId)) want(kidId, kids[i]!, priority, legacyQuadQueue ? null : id);
        }
      }
      draw.push(tile);
      drawIds.add(id);
      const albedo = tile.albedo ? null : inheritFrom(key, HAS_ALBEDO);
      const height = tile.height ? null : inheritFrom(key, HAS_HEIGHT);
      const artificial = tile.artificial ? null : inheritFrom(key, HAS_ARTIFICIAL);
      if (albedo) pinned.add(formatTileKey(albedo.from.key));
      if (height) pinned.add(formatTileKey(height.from.key));
      if (artificial) pinned.add(formatTileKey(artificial.from.key));
      // A drawn tile's own error runs (splitAt/2, splitAt]: half that is where
      // its parent split, and splitAt is where it splits itself.
      drawState.set(id, {
        albedo,
        height,
        artificial,
        morph: clamp(pps / splitAt * 2 - 1, 0, 1),
      });
      if (!legacyTouchOrder) touch(id, tile);
    }

    const rootAcross = tilesAcross(minLevel);
    for (let face = 0; face < FACE_COUNT; face++) {
      for (let x = 0; x < rootAcross; x++) {
        for (let y = 0; y < rootAcross; y++) visit({ body, face, level: minLevel, x, y }, maxLevel);
      }
    }

    // `ringHole`: punch a hole at the horizon band's own grazing edge, the
    // drawn tile whose margin to the split horizon is smallest. Post-process
    // rather than skip it inside `visit`, since which tile that is is only
    // known once every candidate has been walked.
    if (ringHole && draw.length) {
      // Where the view carries `frustumM` a tile reaching here has its 34 km
      // padded cap touching the frame, which is a great deal wider than the
      // frame at SURFACE, so the smallest margin to the split horizon is still
      // the grazing edge of what is drawn rather than a tile known to be on
      // screen. `RING_HOLE_TILES` sorts by that margin so the control is
      // deterministic run to run; `descent-dojo --ship --inject ring-hole`
      // reports what it reads back.
      const margins = draw.map((tile) => {
        const bounds = boundsOf(tile.key, radiusM);
        const g = Math.acos(clamp(dot(bounds.centre, camDir), -1, 1));
        return horizonRad - (g - bounds.angularRadius);
      });
      const order2 = margins.map((_, i) => i).sort((a, b) => margins[a]! - margins[b]!);
      const holeIdxs = new Set(order2.slice(0, Math.min(RING_HOLE_TILES, draw.length)));
      for (let i = draw.length - 1; i >= 0; i--) {
        if (!holeIdxs.has(i)) continue;
        drawIds.delete(formatTileKey(draw[i]!.key));
        draw.splice(i, 1);
      }
    }

    // A stalled id sorts behind fresh work only while it is still within its
    // bounded wait: past `stalledBoundedWaitPasses` passes with no slot, it
    // sorts by priority like everything else, so an active descent whose
    // wanted set never dips under the cap cannot starve it forever.
    const deprioritized = (id: string): boolean => stalled.has(id)
      && (legacyUnboundedStall || (stalledWaitPasses.get(id) ?? 0) < stalledBoundedWaitPasses);

    // The queue: ordered by screen-space error, a hard cap on what may be in
    // flight, and an AbortController on every one.
    const order = [...wanted.values()].sort((a, b) =>
      Number(deprioritized(a.id)) - Number(deprioritized(b.id)) || b.priority - a.priority);
    const cap = fetchHold ? Math.min(maxInFlight, HOLD_MAX_IN_FLIGHT) : maxInFlight;
    // A tile deeper than the hold admits is not dropped, only left in the
    // queue: the traversal wants it again on the pass after the release.
    const admitted = fetchHold && holdMaxLevel < Infinity
      ? order.filter((t) => t.key.level <= holdMaxLevel) : order;
    // The cap is a cap on STARTS, not a membership test the order re-runs every
    // traversal: a request already on the wire that the traversal still wants
    // takes its slot back first, in priority order, because the bytes it holds
    // are spent whether it finishes or not. New starts take what is left.
    // `legacyRingQueue` puts the membership test back.
    const target = legacyRingQueue ? admitted.slice(0, cap)
      : admitStarts(admitted, cap);
    const targetIds = new Set(target.map((t) => t.id));
    queued = order.length - target.length;
    for (const t of order) {
      if (!stalled.has(t.id)) continue;
      if (targetIds.has(t.id)) stalledWaitPasses.delete(t.id);
      else stalledWaitPasses.set(t.id, (stalledWaitPasses.get(t.id) ?? 0) + 1);
    }
    if (fetchHold) heldPasses++;
    for (const [id, req] of [...inFlight]) {
      if (targetIds.has(id)) continue;
      // A hold cutting the cap or clamping the depth is not the order changing
      // its mind, so `superseded` stays the count of what the queue abandoned.
      abort(id, req, !wanted.has(id) ? 'no longer wanted'
        : fetchHold ? 'held for a higher-priority read' : 'superseded');
    }
    for (const t of target) if (!inFlight.has(t.id)) start(t.key, t.id, cap);

    const unmount: LoadedTile[] = [];
    for (const id of drawnIds) {
      if (drawIds.has(id)) continue;
      const tile = resident.get(id);
      if (tile) unmount.push(tile);
    }

    // LRU by bytes, and never what is on screen however old it is, whether the
    // traversal draws it or the caller still holds it, nor a tile with a
    // resident child: the traversal reaches a tile only through its resident
    // parent, so taking the parent first strands the child unreached.
    // Deleting the current entry mid Map-iteration is well defined, so this
    // walks `resident` directly instead of copying it to an array first. A
    // parent passed over for a child taken later in the walk goes on the next.
    const evict: LoadedTile[] = [];
    for (let taken = 1; taken > 0 && residentBytes > budgetBytes;) {
      taken = 0;
      for (const [id, tile] of resident) {
        if (residentBytes <= budgetBytes) break;
        if (drawIds.has(id) || pinned.has(id) || held?.has(id)) continue;
        if (tile.key.level <= coarseKeep) continue;
        if (!legacyEvictParents && childrenOf(tile.key).some((k) => resident.has(formatTileKey(k)))) continue;
        resident.delete(id);
        residentBytes -= tile.bytes;
        evict.push(tile);
        taken++;
      }
    }
    overBudget = residentBytes > budgetBytes;

    const mount: LoadedTile[] = [];
    const keep: LoadedTile[] = [];
    for (const tile of draw) {
      (drawnIds.has(formatTileKey(tile.key)) ? keep : mount).push(tile);
    }
    drawnIds = drawIds;
    pinnedCount = pinned.size;
    return { mount, keep, unmount, evict, draw: drawState };
  }

  return {
    request,
    setFetchHold(holding: boolean, maxLevel?: number): void {
      fetchHold = holding;
      holdMaxLevel = holding ? maxLevel ?? Infinity : Infinity;
    },
    stats(): TileTreeStats {
      return {
        resident: resident.size,
        residentBytes,
        inFlight: inFlight.size,
        queued,
        fetched,
        aborted,
        superseded,
        timedOut,
        failed,
        overBudget,
        complete,
        pinned: pinnedCount,
        fetchHold,
        holdMaxLevel,
        heldPasses,
      };
    },
    residentOrder(): string[] {
      return [...resident.keys()];
    },
    dispose(): void {
      disposed = true;
      for (const [id, req] of [...inFlight]) abort(id, req, 'tile tree disposed');
      inFlight.clear();
      for (const tile of resident.values()) tile.dispose();
      resident.clear();
      residentBytes = 0;
      pinnedCount = 0;
      drawnIds = new Set();
    },
  };
}

// -----------------------------------------------------------------------------

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function scale(a: Vec3, k: number): Vec3 {
  return [a[0] * k, a[1] * k, a[2] * k];
}

function length(a: Vec3): number {
  return Math.hypot(a[0], a[1], a[2]);
}

function normalize(a: Vec3): Vec3 {
  const l = length(a);
  if (l === 0) throw new Error('normalize: zero vector');
  return [a[0] / l, a[1] / l, a[2] / l];
}
